import React, {
  useState, useRef, useEffect, useLayoutEffect,
} from 'react';
import PropTypes from 'prop-types';
import helpers from 'src/helpers';
import { withStyles } from 'tss-react/mui';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router';
import { logout, pollWhatsNewStatus } from 'src/actions/accountActions';
import NavBar from 'src/layouts/DashboardLayout/NavBar';
import TopBar from 'src/layouts/DashboardLayout/TopBar';
import RedirectLogic from 'src/RedirectLogic';
import Notification from 'src/containers/Notification';
import TOSAndPrivacyStatement from 'src/containers/TOSAndPrivacyStatement';
import AdminSignInAs from 'src/containers/AdminSignInAs';
import ChangeCompanyTitle from 'src/containers/ChangeCompanyTitle';
import EmailTokenManager from 'src/containers/EmailTokenManager';
import EnableCPTexting from 'src/containers/EnableCPTexting';
import PBTextingModal from 'src/containers/PBTextingModal';
import TelnyxMigrationPinModal from 'src/containers/TelnyxMigrationPinModal';
import WhatsNewModal from 'src/containers/WhatsNewModal';
import ShareSetUpdatesModal from 'src/containers/ShareSetUpdatesModal';
import EmailFilterNoticeModal from 'src/containers/EmailFilterNoticeModal';
import ChangeCampaigns from 'src/containers/ChangeCampaigns';
import DashboardLayoutTopMessage from 'src/layouts/DashboardLayout/DashboardLayoutTopMessage';
import VersionChecker from 'src/components/VersionChecker';
import { clearPostLoginRedirect } from 'src/helpers/postLoginRedirect';

import {
  setCurrentScrollDashboardLayoutContentPosition,
  clearMovingScrollDashboardLayoutContentToPosition,
} from 'src/actions/scrollActions';

import dashboardLayoutStyle from 'src/layouts/DashboardLayout/dashboardLayout.style';

function DashboardLayout({ children, classes }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const scrollContentRef = useRef();
  const rootRef = useRef(null);
  const topMessageRef = useRef(null);

  const [isMobileNavOpen, setMobileNavOpen] = useState(false);
  const [isDesktopNavOpen, setDesktopNavOpen] = useState(false);
  const wrapperDesktopNavClass = !isDesktopNavOpen ? ' wrapperWithSmallNabBar' : '';

  const {
    currentScrollDashboardLayoutContentPosition,
    moveScrollDashboardLayoutContentToPosition,
  } = useSelector((state) => state?.scroll || {});

  const {
    shouldSave,
  } = currentScrollDashboardLayoutContentPosition || {};

  const {
    x: moveToX,
    y: moveToY,
    shouldStart: shouldStartMoveTo,
  } = moveScrollDashboardLayoutContentToPosition || {};

  const setLogout = async (toDoSomethingInTry = null) => {
    try {
      await dispatch(logout());
      history.push('/');
      // A deliberate sign-out must not send the next sign-in back to the
      // page this user was on (AuthGuard may have recorded it while the
      // signed-out state rendered).
      clearPostLoginRedirect();
      if (toDoSomethingInTry) {
        toDoSomethingInTry();
      }
    } catch (error) {
      enqueueSnackbar('Unable to logout', {
        variant: 'error'
      });
    }
  };

  useEffect(() => {
    // call dispatch(saveCurrentScrollDashboardLayoutContentPosition(true));
    // dispatch(saveCurrentScrollPerfectScrollbarTablePosition(true));
    // if you want to save current position
    if (shouldSave && scrollContentRef) {
      const x = helpers.transform.toInt10(scrollContentRef.current.scrollLeft);
      const y = helpers.transform.toInt10(scrollContentRef.current.scrollTop);
      dispatch(setCurrentScrollDashboardLayoutContentPosition(x, y));
    }
  }, [shouldSave, scrollContentRef]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (shouldStartMoveTo && scrollContentRef) {
      scrollContentRef.current.scrollLeft = helpers.transform.toInt10(moveToX || 0);
      scrollContentRef.current.scrollTop = helpers.transform.toInt10(moveToY || 0);
      dispatch(clearMovingScrollDashboardLayoutContentToPosition());
    }
  }, [shouldStartMoveTo, scrollContentRef]);

  // Publish the top-message bar's rendered height as --cp-top-banner-height
  // on the layout root, so descendants can subtract it.
  //
  // The bar itself no longer overlaps anything (it sits above the scrolling
  // container rather than inside it), and everything that sizes itself
  // against its PARENT already adapts for free. This variable exists for the
  // handful of boxes that size themselves against the VIEWPORT instead --
  // ChatView's calc(100vh - 88px) and the Smart Form builder's
  // calc(100vh - 240px) -- which have no other way to know the bar is there
  // and would otherwise overrun the scroller by exactly the bar's height.
  //
  // Measured rather than hardcoded because the height depends on how far the
  // text wraps, which depends on the viewport width, which banner is showing
  // and how long its message is. A ResizeObserver tracks all of that. The
  // wrapper div is always rendered, so when no banner is showing it measures
  // 0 and the variable is 0px -- every consumer then resolves to exactly
  // what it computed before this existed.
  useLayoutEffect(() => {
    const bannerEl = topMessageRef.current;
    const rootEl = rootRef.current;
    if (!bannerEl || !rootEl) {
      return undefined;
    }

    const publishHeight = () => {
      const { height } = bannerEl.getBoundingClientRect();
      rootEl.style.setProperty('--cp-top-banner-height', `${Math.round(height || 0)}px`);
    };

    // useLayoutEffect so the first measurement lands before paint. The
    // viewport-height consumers do not self-correct the way a flex child
    // does, so measuring after paint would show them one wrong frame.
    publishHeight();

    // Also listen for viewport resize. ResizeObserver covers this on its own
    // in a normal browser tab, but it is delivered on the rendering lifecycle,
    // so a throttled or background document can leave the value stale after a
    // rotation. The listener is cheap and makes the fallback path -- and the
    // no-ResizeObserver path below -- correct too.
    window.addEventListener('resize', publishHeight);

    if (typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', publishHeight);
    }

    const observer = new ResizeObserver(publishHeight);
    observer.observe(bannerEl);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', publishHeight);
    };
  }, []);

  // Poll for What's New status on route changes (every 20 minutes)
  const lastWhatsNewPollRef = useRef(0);
  const WHATS_NEW_POLL_INTERVAL_MS = 20 * 60 * 1000; // 20 minutes
  useEffect(() => {
    const unlisten = history.listen(() => {
      const now = Date.now();
      if (now - lastWhatsNewPollRef.current >= WHATS_NEW_POLL_INTERVAL_MS) {
        lastWhatsNewPollRef.current = now;
        dispatch(pollWhatsNewStatus());
      }
    });
    return () => unlisten();
  }, [dispatch, history]);

  return (
    <div className={classes.root} ref={rootRef}>
      <TopBar
        onMobileNavOpen={() => setMobileNavOpen(true)}
        logout={setLogout}
      />
      <NavBar
        onMobileClose={() => setMobileNavOpen(false)}
        openMobile={isMobileNavOpen}
        isDesktopNavOpen={isDesktopNavOpen}
        setDesktopNavOpen={setDesktopNavOpen}
        logout={setLogout}
      />
      <div className={`${classes.wrapper}${wrapperDesktopNavClass}`}>
        <div className={classes.contentContainer}>
          <div className={classes.topMessage} ref={topMessageRef}>
            <DashboardLayoutTopMessage />
          </div>
          <div
            id="firstContentScroll"
            ref={scrollContentRef}
            className={classes.content}
          >
            {children}
          </div>
        </div>
      </div>
      <RedirectLogic />
      <AdminSignInAs logout={setLogout} />
      <ChangeCompanyTitle logout={setLogout} />
      <EmailTokenManager />
      <EnableCPTexting />
      <PBTextingModal />
      <TelnyxMigrationPinModal />
      <WhatsNewModal />
      <ShareSetUpdatesModal />
      <EmailFilterNoticeModal />
      <ChangeCampaigns classes={classes} logout={setLogout} />
      <TOSAndPrivacyStatement logout={setLogout} />
      <Notification />
      <VersionChecker />
    </div>
  );
}

DashboardLayout.propTypes = {
  classes: PropTypes.object.isRequired,
  children: PropTypes.any
};

export default withStyles(DashboardLayout, dashboardLayoutStyle, { withTheme: true });
